/**
 * Footer — standard footer with Privacy Policy, Terms of Service,
 * and Security & Compliance links. Appears on every page via AppShell.
 *
 * Links open modal dialogs with policy content. The content is
 * placeholder text — replace with real legal copy when available.
 */

import { type FC, useState } from 'react';
import { Shield, FileText, Lock } from 'lucide-react';
import { Dialog } from '../primitives/Dialog';

export const Footer: FC = () => {
  const [openModal, setOpenModal] = useState<'privacy' | 'terms' | 'security' | null>(null);
  const year = new Date().getFullYear();

  return (
    <>
      <footer className="relative z-10 border-t border-outline-variant bg-surface-container-lowest">
        <div className="px-5 py-2 flex flex-col sm:flex-row items-center justify-between gap-2">
          {/* Left: Company + copyright */}
          <div className="text-micro text-text-tertiary">
            © {year} Axis Finance Limited. All rights reserved.
          </div>

          {/* Center: Policy links */}
          <div className="flex items-center gap-1">
            <FooterLink icon={<Shield className="w-3 h-3" />} label="Privacy Policy" onClick={() => setOpenModal('privacy')} />
            <span className="text-text-muted text-micro">·</span>
            <FooterLink icon={<FileText className="w-3 h-3" />} label="Terms of Service" onClick={() => setOpenModal('terms')} />
            <span className="text-text-muted text-micro">·</span>
            <FooterLink icon={<Lock className="w-3 h-3" />} label="Security & Compliance" onClick={() => setOpenModal('security')} />
          </div>

          {/* Right: Product */}
          <div className="text-micro text-text-muted">
            Powered by <span className="font-medium text-text-tertiary">Agentic Loan Audit</span> v1.0
          </div>
        </div>
      </footer>

      {/* Policy modals — Dialog primitive provides focus trap, Esc-to-close,
          role="dialog", and an accessible close button. */}
      <Dialog
        open={openModal !== null}
        onOpenChange={(open) => { if (!open) setOpenModal(null); }}
        title={(openModal && POLICY_CONTENT[openModal]?.title) || ''}
        description="Axis Finance Limited — Agentic Loan Audit Platform"
        size="lg"
        footer={
          <span className="text-micro text-slate-400 mr-auto">
            Last updated: January 2025. For questions, contact legal@axisfinance.com
          </span>
        }
      >
        {openModal &&
          POLICY_CONTENT[openModal]?.sections.map((section, i) => (
            <div key={i}>
              <h3 className="text-label font-semibold text-slate-800 mb-1.5">
                {i + 1}. {section.heading}
              </h3>
              <p className="text-caption text-slate-600 leading-relaxed">{section.body}</p>
            </div>
          ))}
      </Dialog>
    </>
  );
};

// -------------------------------------------------------------------------
// Footer link
// -------------------------------------------------------------------------

function FooterLink({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-micro text-text-secondary hover:text-brand-blue hover:bg-brand-blue/5 transition-colors"
    >
      {icon}
      {label}
    </button>
  );
}

// -------------------------------------------------------------------------
// Policy modal
// -------------------------------------------------------------------------

const POLICY_CONTENT: Record<string, { title: string; icon: React.ReactNode; sections: Array<{ heading: string; body: string }> }> = {
  privacy: {
    title: 'Privacy Policy',
    icon: <Shield className="w-5 h-5 text-brand-blue" />,
    sections: [
      {
        heading: 'Data Collection',
        body: 'Agentic Loan Audit collects and processes loan file documents (CAM reports, sanction letters, KFS, bank statements, salary slips, KYC documents), LOS data exports (JSON), and user account information (name, email, role) required for platform operation. All data is collected with the explicit consent of the uploading auditor and the originating financial institution.',
      },
      {
        heading: 'Data Processing',
        body: 'Uploaded documents are processed through OCR (Optical Character Recognition) and AI-based extraction to derive structured fields. Bank statements may be analysed by third-party verification providers (Novel Check) for salary credit verification and tamper detection. All processing is performed solely for the purpose of loan file audit and compliance verification.',
      },
      {
        heading: 'Data Storage & Retention',
        body: 'All data is stored in encrypted databases (AES-256 at rest) hosted on secure cloud infrastructure. Document files are stored in S3-compatible object storage with server-side encryption. Data is retained for the period required by RBI regulations and internal audit policy (typically 8 years from loan closure). Users may request data deletion subject to regulatory retention requirements.',
      },
      {
        heading: 'Data Sharing',
        body: 'Personal and financial data is never sold or shared with third parties for marketing purposes. Data may be shared with: (a) third-party verification providers (Novel Check, Perfios, FinBox) solely for bank statement analysis, (b) cloud infrastructure providers (AWS, Render) for hosting purposes, (c) regulatory authorities when required by law.',
      },
      {
        heading: 'Your Rights (DPDPA 2023)',
        body: 'Under the Digital Personal Data Protection Act 2023, data principals have the right to: access their personal data, request correction of inaccurate data, request erasure (subject to regulatory retention), and lodge grievances. Contact the Data Protection Officer at dpo@axisfinance.com for any privacy-related requests.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    icon: <FileText className="w-5 h-5 text-emerald-600" />,
    sections: [
      {
        heading: 'Acceptance of Terms',
        body: 'By accessing and using the Agentic Loan Audit platform, you agree to be bound by these Terms of Service. This platform is provided exclusively for authorized employees and agents of Axis Finance Limited and its affiliated entities for the purpose of loan file audit and compliance verification.',
      },
      {
        heading: 'Authorized Use',
        body: 'Users must: (a) use the platform only for legitimate audit and compliance purposes, (b) maintain the confidentiality of borrower data accessed through the platform, (c) not share login credentials or access tokens, (d) report any security incidents or unauthorized access immediately, (e) comply with all applicable RBI regulations and internal policies regarding data handling.',
      },
      {
        heading: 'Data Accuracy',
        body: 'The platform uses AI-based extraction and rule engines to analyse loan documents. While designed for high accuracy, AI outputs should be treated as decision-support tools, not final verdicts. Auditors are responsible for exercising professional judgment when reviewing findings and making audit decisions. The platform provides evidence and flags — the auditor makes the call.',
      },
      {
        heading: 'Intellectual Property',
        body: 'The Agentic Loan Audit platform, including its software, algorithms, UI design, documentation, and branding, is the intellectual property of Axis Finance Limited. Users are granted a non-transferable, non-exclusive license to use the platform for authorized business purposes only.',
      },
      {
        heading: 'Limitation of Liability',
        body: 'Axis Finance Limited shall not be liable for any indirect, incidental, or consequential damages arising from the use of this platform. The platform is provided "as is" with commercially reasonable efforts towards accuracy and availability. Service availability targets are 99.5% uptime measured monthly.',
      },
    ],
  },
  security: {
    title: 'Security & Compliance',
    icon: <Lock className="w-5 h-5 text-amber-600" />,
    sections: [
      {
        heading: 'Data Encryption',
        body: 'All data is encrypted at rest using AES-256 encryption. Data in transit is protected by TLS 1.3. Database connections use SSL. S3 object storage uses server-side encryption (SSE-S3). Encryption keys are managed through AWS KMS with automatic rotation.',
      },
      {
        heading: 'Access Control',
        body: 'The platform implements role-based access control (RBAC) with 5 built-in roles: Admin, Auditor, Credit Manager, Branch Head, and Viewer. Each role has granular permissions across 47 permission types. All actions are logged in an immutable audit trail with actor identity, timestamp, and IP address. Single Sign-On (SSO) via Microsoft 365 / Office 365 is supported.',
      },
      {
        heading: 'Infrastructure Security',
        body: 'The platform is hosted on Render (US-based PaaS) with automatic SSL, DDoS protection, and private networking. Database is PostgreSQL with automated backups (daily, 30-day retention). File storage uses AWS S3 with versioning enabled. All infrastructure components are in SOC 2 Type II certified data centres.',
      },
      {
        heading: 'Audit Trail',
        body: 'Every action on the platform is recorded in a tamper-proof audit trail: document uploads, LOS data submissions, validation runs, finding resolutions, user logins, role changes, and configuration updates. Audit logs are retained for 10 years and are available for regulatory inspection.',
      },
      {
        heading: 'RBI Compliance',
        body: 'The platform is designed to support compliance with: RBI Master Direction on IT Governance (2023), RBI Guidelines on Digital Lending (2022), DPDPA 2023 (Digital Personal Data Protection Act), and internal audit policies of Axis Finance Limited. The checklist-based audit approach aligns with RBI inspection methodologies.',
      },
      {
        heading: 'Incident Response',
        body: 'A documented incident response plan is in place covering: detection, containment, eradication, recovery, and post-incident review. Security incidents are reported to the CISO within 1 hour and to RBI/CERT-In within the mandated timeframe. Contact security@axisfinance.com to report vulnerabilities.',
      },
    ],
  },
};

